// frontend/src/lib/eventDaySpan.js
//
// Which FIRM-LOCAL days a calendar item covers, for the printouts that list
// a multi-day item on every day it covers (James 2026-10-21: "Every day it
// covers"): the Calendar's paper schedules (lib/scheduleLayout.js) and the
// Appointments List / Appointments + ToDos reports (lib/reportAppointments.js).
// Before, a 3-day deposition printed only on its start day, so a printout
// starting on day 2 missed it.  The rows come from GET /api/calendar
// ?overlap=true, which also returns an item still running at the range's
// first midnight (api/src/lib/eventRange.js) — the same rule as here.
//
// The rule (end-EXCLUSIVE — an item covers [start_time, end_time)):
//   - The first day is the firm-local day of start_time.
//   - With end_time after start_time, the last day is the firm-local day of
//     the instant just before end_time: an item ending exactly at the firm's
//     midnight does not spill onto the next day (Amicus stores most all-day
//     items as 00:00 → 00:00; 272 of the 290 items longer than a day in
//     production, 2026-10-21).
//   - A NULL, equal (zero-length) or earlier end_time (861 rows end before
//     they start, 2026-10-21) leaves the item on its start day only.
// Days are counted on the firm's calendar, never the browser's (a viewer in
// Taipei prints the firm's days), and across daylight saving the firm day
// is what Intl says it is (utils/eventTime.js) — never fixed 24-hour steps.
//
// "Day N of M": N = which of the item's days this is (its start day is Day
// 1), M = every day it covers, including days outside the printed range.
// All-day = starts at exactly the firm's midnight (the Amicus all-day / to-do
// form; scheduleLayout.isAllDayItem and utils/todoDue.js use the same test).
//
// DEADLINE TO-DOS print ONCE, on their due day (James 2026-10-21, "Due date
// only") — printDaysInRange below; the same printouts' rows go through it.
// Amicus stores a to-do from the day work starts to the day it is DUE, both
// at the firm's midnight: event 320705 "CMC Statement Due 12/3/26" (Power
// One v. ECG) runs 2026-11-30 00:00 → 2026-12-03 00:00 PT, so its due day is
// the firm-local date OF end_time (12/3), not the day before it (which is
// where the end-exclusive span above stops).  Printed on every day it
// "covered", the 272 such items listed an average of 31 days each, up to 894.
//
// isDeadlineTodo is THE classifier.  Its SQL twin is api/src/lib/eventRange.js
// deadlineTodoSql / deadlineTodoDueDaySql (the To-Dos report's due window and
// the overlap fetch); both run the one case table
// lib/testSupport/deadlineTodoCases.json (eventDaySpan.test.js here,
// api/src/tests/calendarTodoDueDay.test.js there).  An item is a deadline
// to-do when:
//   - created in Omnia (no amicus_id): it has a due_date (J3, as before);
//   - imported (amicus_id set): Amicus filed it as a to-do
//     (amicus_event_kind 'todo'); 'appointment' never is; NULL (not known
//     until the next Amicus import fills the column) is one when its start
//     AND end are both exactly the firm's midnight (read to the minute, like
//     isAllDayItem) and it spans more than one day: end's firm date at least
//     two days after start's.  An exactly-one-day 00:00 → 00:00 item stays an
//     all-day item on its start day.
// Its due day: due_date when set; else the firm-local date of end_time when
// the end is at or after the start; else (no end, or one before the start —
// 858 imported rows, all but one completed, some at Amicus's 1900-01-01) the
// start day.  Its time: the start time when the item starts on its due day
// at a time other than midnight (where the Calendar shows it); else none.

import { firmDateIso, utcToLocalInput, firmTz } from '../utils/eventTime'

const DAY_MS = 24 * 60 * 60 * 1000
const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

// 'YYYY-MM-DD' ↔ a day number (UTC calendar arithmetic, no zone involved).
function dayNumber (iso) {
  const m = ISO_DAY_RE.exec(String(iso || ''))
  if (!m) return NaN
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS
}
function isoOfDayNumber (n) {
  const t = new Date(n * DAY_MS)
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`
}

// "HH:MM" (24 h) → "9:05 AM".  By hand: Intl can put U+202F before AM/PM,
// which jsPDF cannot draw (rules/reports.md).
export function spanClock (hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return ''
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

/**
 * The firm-local days an item covers, or null without a readable start_time.
 *   { startDay, lastDay: 'YYYY-MM-DD', dayCount, multiDay,
 *     startTime: 'HH:MM', allDay,
 *     endTime: 'HH:MM' when the item ends partway through its last day, else '',
 *     hasEnd: end_time is after start_time }
 */
export function eventDaySpan (ev, tz = firmTz()) {
  const startMs = ev && ev.start_time ? new Date(ev.start_time).getTime() : NaN
  if (Number.isNaN(startMs)) return null
  const startLocal = utcToLocalInput(ev.start_time, tz)
  if (!startLocal) return null
  const [startDay, startTime] = startLocal.split('T')
  const allDay = startTime === '00:00'
  let lastDay = startDay
  let endTime = ''
  const endMs = ev.end_time ? new Date(ev.end_time).getTime() : NaN
  if (!Number.isNaN(endMs) && endMs > startMs) {
    // The instant just before the end: an end at the firm's midnight closes
    // the day before it.
    lastDay = firmDateIso(endMs - 1, tz) || startDay
    const [endDay, endClock] = utcToLocalInput(ev.end_time, tz).split('T')
    if (endDay === lastDay) endTime = endClock
  }
  const dayCount = Math.max(1, dayNumber(lastDay) - dayNumber(startDay) + 1)
  return {
    startDay, lastDay, dayCount, multiDay: dayCount > 1, startTime, allDay, endTime,
    hasEnd: !Number.isNaN(endMs) && endMs > startMs,
  }
}

/** Which of the span's days `day` is (its start day = 1), or 0 outside it. */
export function spanDayIndex (span, day) {
  if (!span) return 0
  const n = dayNumber(day) - dayNumber(span.startDay) + 1
  return Number.isFinite(n) && n >= 1 && n <= span.dayCount ? n : 0
}

/**
 * The days of [from, to] ('YYYY-MM-DD', inclusive; either may be omitted)
 * that the item covers, in order: [{ day, n, of }].  A single-day item gives
 * its start day (n = of = 1) when that day is in range.  Enumerates only the
 * days inside the range, so a long item costs no more than the range.
 */
export function eventDaysInRange (ev, { from, to, tz = firmTz() } = {}) {
  const span = eventDaySpan(ev, tz)
  if (!span) return []
  const first = dayNumber(span.startDay)
  const lo = Math.max(first, from ? dayNumber(from) : first)
  const hi = Math.min(dayNumber(span.lastDay), to ? dayNumber(to) : dayNumber(span.lastDay))
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return []
  const out = []
  for (let d = lo; d <= hi; d++) out.push({ day: isoOfDayNumber(d), n: d - first + 1, of: span.dayCount })
  return out
}

/** "Day 2 of 3" */
export const spanDayLabel = (n, of) => `Day ${n} of ${of}`

/**
 * On day `n` of a multi-day span: does the item start that day, end partway
 * through it, and is it an all-day item that day?  An all-day item whose end
 * is 11:59 PM (34 rows in production, 2026-10-21) ends with its day, not
 * partway.
 */
export function spanDayParts (span, n) {
  const starts = n === 1
  const ends = n === span.dayCount && !!span.endTime && !(span.allDay && span.endTime === '23:59')
  return { starts, ends, allDayHere: span.allDay && !ends }
}

// ── Deadline to-dos (see the header) ─────────────────────────────────────

/** The label a deadline to-do prints with on its due day. */
export const DUE_LABEL = 'Due'

const DUE_DATE_RE = /^(\d{4}-\d{2}-\d{2})/

// [firm-local day 'YYYY-MM-DD', 'HH:MM'] of a timestamp, or null.
function firmParts (value, tz) {
  if (value === null || value === undefined || value === '') return null
  if (Number.isNaN(new Date(value).getTime())) return null
  const local = utcToLocalInput(value, tz)
  return local ? local.split('T') : null
}

// Imported = has an amicus_id (the SQL twin: NULLIF(amicus_id, '') IS NOT NULL).
const isImported = (ev) => ev.amicus_id !== null && ev.amicus_id !== undefined && String(ev.amicus_id) !== ''

/**
 * Is the item a deadline to-do — printed once, on its due day?  (The rule is
 * in the header; api/src/lib/eventRange.js deadlineTodoSql is its twin.)
 */
export function isDeadlineTodo (ev, tz = firmTz()) {
  if (!ev) return false
  if (!isImported(ev)) return !!ev.due_date
  const kind = ev.amicus_event_kind
  if (kind === 'todo') return true
  if (kind !== null && kind !== undefined) return false
  const s = firmParts(ev.start_time, tz)
  const e = firmParts(ev.end_time, tz)
  if (!s || !e) return false
  return s[1] === '00:00' && e[1] === '00:00' && dayNumber(e[0]) - dayNumber(s[0]) >= 2
}

/**
 * The firm-local day ('YYYY-MM-DD') a to-do is due: due_date; else end_time's
 * firm date when the end is at or after the start; else the start day.  ''
 * without a readable day.
 */
export function deadlineTodoDueDay (ev, tz = firmTz()) {
  if (!ev) return ''
  if (ev.due_date) {
    const m = DUE_DATE_RE.exec(String(ev.due_date))
    if (m) return m[1]
  }
  const s = firmParts(ev.start_time, tz)
  if (!s) return ''
  const e = firmParts(ev.end_time, tz)
  return e && new Date(ev.end_time).getTime() >= new Date(ev.start_time).getTime() ? e[0] : s[0]
}

/**
 * A deadline to-do's time on its due day ('HH:MM'), or '': its start time
 * when it starts on the due day at a time other than midnight.
 */
export function deadlineTodoTime (ev, tz = firmTz()) {
  if (!ev) return ''
  const s = firmParts(ev.start_time, tz)
  if (!s || s[1] === '00:00') return ''
  return s[0] === deadlineTodoDueDay(ev, tz) ? s[1] : ''
}

/**
 * The days an item PRINTS on in [from, to] (inclusive 'YYYY-MM-DD'; either
 * may be omitted): [{ day, n, of, due }].  A deadline to-do: once, on its due
 * day when that day is in range ({ n: 1, of: 1, due: true }) — never on the
 * days before it, and not at all when it is due after the range.  Any other
 * item: every day it covers (eventDaysInRange, due: false).
 */
export function printDaysInRange (ev, { from, to, tz = firmTz() } = {}) {
  if (!ev || !ev.start_time) return []
  if (isDeadlineTodo(ev, tz)) {
    const day = deadlineTodoDueDay(ev, tz)
    if (!day || (from && day < from) || (to && day > to)) return []
    return [{ day, n: 1, of: 1, due: true }]
  }
  return eventDaysInRange(ev, { from, to, tz }).map(d => ({ ...d, due: false }))
}
